import { useCallback, useState, useRef, useEffect } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Subject, debounceTime, switchMap, catchError, EMPTY } from 'rxjs';
import { useClient } from 'shared/hooks/use-client/use-client.hook';
import type { OwnerParams } from 'shared/types';
import { Visitor as ApiVisitor } from 'store/api-client/api-client.types';
import { CreateVisitFormV2Values } from '../../../../../types/form-data.types';

const DEBOUNCE_TIME_MS = 500;
const MIN_QUERY_LENGTH = 2;

export interface UseVisitorTypeaheadOptions {
  setValue: UseFormSetValue<CreateVisitFormV2Values>;
  visitorIndex: number;
}

export interface UseVisitorTypeaheadReturn {
  visitorOptions: ApiVisitor[];
  isLoading: boolean;
  isDropdownVisible: boolean;
  searchVisitor: (query: string) => void;
  clearSearch: () => void;
  selectVisitor: (visitor: ApiVisitor) => void;
  openDropdown: () => void;
  closeDropdown: () => void;
}

export const useVisitorTypeahead = ({
  setValue,
  visitorIndex,
}: UseVisitorTypeaheadOptions): UseVisitorTypeaheadReturn => {
  const client = useClient();
  const { ownerId } = useParams<OwnerParams>();

  const [visitors, setVisitors] = useState<ApiVisitor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const searchSubjectRef = useRef(new Subject<string>());

  useEffect(() => {
    const subscription = searchSubjectRef.current
      .pipe(
        debounceTime(DEBOUNCE_TIME_MS),
        switchMap((query) => {
          if (!query || query.length < MIN_QUERY_LENGTH) {
            setVisitors([]);
            setIsLoading(false);
            return EMPTY;
          }

          return client.fetchVisitors({ query, buildingId: ownerId || '' }).pipe(
            catchError(() => {
              setVisitors([]);
              setIsLoading(false);
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe(({ response }) => {
        setVisitors(response.data);
        setIsLoading(false);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [client, ownerId]);

  const searchVisitor = useCallback((query: string) => {
    setIsLoading(true);
    setIsDropdownVisible(true);
    searchSubjectRef.current.next(query);
  }, []);

  const clearSearch = useCallback(() => {
    setVisitors([]);
    setIsDropdownVisible(false);
  }, []);

  const selectVisitor = useCallback(
    (visitor: ApiVisitor) => {
      const visitorPrefix = `visitors.${visitorIndex}` as const;

      setValue(`${visitorPrefix}.firstName` as 'visitors.0.firstName', visitor.first_name || '', {
        shouldValidate: true,
      });
      setValue(`${visitorPrefix}.lastName` as 'visitors.0.lastName', visitor.last_name || '', { shouldValidate: true });
      setValue(`${visitorPrefix}.email` as 'visitors.0.email', visitor.email || '', { shouldValidate: true });
      setValue(`${visitorPrefix}.phone` as 'visitors.0.phone', visitor.phone || '', { shouldValidate: true });

      setIsDropdownVisible(false);
      setVisitors([]);
    },
    [setValue, visitorIndex],
  );

  const openDropdown = useCallback(() => {
    if (visitors.length > 0) {
      setIsDropdownVisible(true);
    }
  }, [visitors.length]);

  const closeDropdown = useCallback(() => {
    setIsDropdownVisible(false);
  }, []);

  return {
    visitorOptions: visitors,
    isLoading,
    isDropdownVisible,
    searchVisitor,
    clearSearch,
    selectVisitor,
    openDropdown,
    closeDropdown,
  };
};
